import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, IconButton, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Chip, Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import CollectionsIcon from "@mui/icons-material/Collections";
import ShareIcon from "@mui/icons-material/Share";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GroupIcon from "@mui/icons-material/Group";
import DownloadIcon from "@mui/icons-material/Download";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VideocamIcon from "@mui/icons-material/Videocam";
import { AuthContext } from "../context/AuthContext";
import Header from "../components/Header";
import { getMediaUrl, getMyMedia, uploadImage, triggerSingleDownload, downloadMediaZip, updateLabel } from "../service/mediaApi";
import {
  getAlbum, addMediaToAlbum, removeMediaFromAlbum,
  shareAlbumWithUser, shareAlbumWithGroup, removeShare, getMyGroups,
} from "../service/albumApi";
import { useSnack } from "../hooks/useSnack";

// ─── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ images, startIndex, onClose }) {
  const [current, setCurrent] = useState(startIndex);
  const goPrev = useCallback(() => setCurrent((i) => (i - 1 + images.length) % images.length), [images.length]);
  const goNext = useCallback(() => setCurrent((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, goPrev, goNext]);

  const img = images[current];
  return (
    <Box onClick={onClose} sx={{
      position: "fixed", inset: 0, zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
      backgroundColor: "rgba(0,0,0,0.9)", backdropFilter: "blur(8px)",
    }}>
      <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16, color: "white",
        bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" }, zIndex: 10001 }}>
        <CloseIcon />
      </IconButton>
      {images.length > 1 && (
        <IconButton onClick={(e) => { e.stopPropagation(); goPrev(); }} sx={{
          position: "absolute", left: 16, color: "white",
          bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" }, zIndex: 10001 }}>
          <ArrowBackIosNewIcon />
        </IconButton>
      )}
      <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        {img.mime_type?.startsWith("video/") ? (
          <Box key={img.ID_media} component="video"
            src={getMediaUrl(img.public_id)}
            controls autoPlay
            sx={{ maxWidth: "90vw", maxHeight: "80vh", borderRadius: 2, boxShadow: "0 8px 60px rgba(0,0,0,0.8)" }}
          />
        ) : (
          <Box component="img" src={getMediaUrl(img.public_id)} alt={img.label || "image"} sx={{
            maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain",
            borderRadius: 2, boxShadow: "0 8px 60px rgba(0,0,0,0.8)" }} />
        )}
        {img.label && (
          <Typography sx={{ mt: 1.5, color: "rgba(255,255,255,0.85)", fontSize: 14, fontStyle: "italic" }}>
            {img.label}
          </Typography>
        )}
        {images.length > 1 && (
          <Typography sx={{ mt: 0.5, color: "rgba(255,255,255,0.45)", fontSize: 12 }}>
            {current + 1} / {images.length}
          </Typography>
        )}
      </Box>
      {images.length > 1 && (
        <IconButton onClick={(e) => { e.stopPropagation(); goNext(); }} sx={{
          position: "absolute", right: 16, color: "white",
          bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" }, zIndex: 10001 }}>
          <ArrowForwardIosIcon />
        </IconButton>
      )}
    </Box>
  );
}

// ─── Dialogue ajout photos depuis Ma galerie ──────────────────────────────────
function AddFromGalleryDialog({ open, onClose, albumMediaIds, onAdd }) {
  const [allMedia, setAllMedia] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const col = theme.custom.mymodal;

  useEffect(() => {
    if (open) {
      setSelected([]);
      getMyMedia().then(setAllMedia).catch(() => {});
    }
  }, [open]);

  const available = allMedia.filter((m) => !albumMediaIds.includes(m.ID_media));

  const toggle = (id) => setSelected((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  );

  const handleAdd = async () => {
    if (!selected.length) return;
    setLoading(true);
    try {
      await onAdd(selected);
      onClose();
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: col.main, color: col.text } }}>
      <DialogTitle sx={{ color: col.text }}>
        Ajouter depuis Ma galerie
        {selected.length > 0 && <Chip label={`${selected.length} sélectionnée(s)`} size="small"
          sx={{ ml: 1.5, bgcolor: col.button, color: "white", fontSize: "0.72rem" }} />}
      </DialogTitle>
      <DialogContent>
        {available.length === 0 ? (
          <Typography sx={{ color: col.text, opacity: 0.5 }}>
            Tous tes médias sont déjà dans cet album, ou ta galerie est vide.
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 1 }}>
            {available.map((m) => {
              const sel = selected.includes(m.ID_media);
              return (
                <Box key={m.ID_media} onClick={() => toggle(m.ID_media)} sx={{
                  position: "relative", cursor: "pointer", borderRadius: 1,
                  outline: sel ? `2px solid ${col.button}` : "2px solid transparent",
                  transition: "outline 0.12s",
                }}>
                  <Box component="img" src={getMediaUrl(m.public_id)} alt={m.label || ""}
                    sx={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 1,
                      filter: sel ? "brightness(0.75)" : "none" }} />
                  {sel && (
                    <Box sx={{ position: "absolute", top: 4, right: 4, width: 20, height: 20,
                      bgcolor: col.button, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <CloseIcon sx={{ color: "white", fontSize: "0.85rem", transform: "rotate(45deg)" }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: col.text }}>Annuler</Button>
        <Button onClick={handleAdd} variant="contained" disabled={!selected.length || loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ bgcolor: col.button }}>
          Ajouter {selected.length > 0 ? `(${selected.length})` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Dialogue partage ─────────────────────────────────────────────────────────
function ShareDialog({ open, onClose, album, onShareAdded, onShareRemoved }) {
  const [email, setEmail] = useState("");
  const [myGroups, setMyGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const theme = useTheme();
  const col = theme.custom.mymodal;
  const { showSnack, Snack } = useSnack();

  useEffect(() => {
    if (open) {
      setEmail("");
      setLoadingGroups(true);
      getMyGroups()
        .then((g) => setMyGroups(g.owned || []))
        .catch(() => {})
        .finally(() => setLoadingGroups(false));
    }
  }, [open]);

  const handleShareUser = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const share = await shareAlbumWithUser(album.album_id, email.trim());
      onShareAdded({ ...share, type: "user" });
      setEmail("");
      showSnack("Album partagé !", "success");
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSubmitting(false); }
  };

  const handleShareGroup = async (groupId) => {
    setSubmitting(true);
    try {
      const share = await shareAlbumWithGroup(album.album_id, groupId);
      onShareAdded({ ...share, type: "group" });
      showSnack("Album partagé avec le groupe !", "success");
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSubmitting(false); }
  };

  const handleRemoveShare = async (shareId) => {
    try {
      await removeShare(album.album_id, shareId);
      onShareRemoved(shareId);
    } catch (e) { showSnack(e.message, "error"); }
  };

  const shares = album?.shares || [];
  const sharedGroupIds = new Set(shares.filter((s) => s.sharedWithGroup).map((s) => s.sharedWithGroup?.group_id));

  if (!album) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: col.main, color: col.text } }}>
        <DialogTitle sx={{ color: col.text, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ShareIcon />
            Partager "{album.name}"
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: col.text }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Partages existants */}
          {shares.length > 0 && (
            <Box>
              <Typography sx={{ color: col.text, fontWeight: 600, mb: 1, fontSize: "0.85rem" }}>
                Partagé avec
              </Typography>
              {shares.map((s) => (
                <Box key={s.id} sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  py: 0.8, borderBottom: `1px solid ${col.text}22`,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {s.sharedWithUser ? (
                      <>
                        <PersonAddIcon sx={{ color: col.text, opacity: 0.5, fontSize: "1rem" }} />
                        <Box>
                          <Typography sx={{ color: col.text, fontSize: "0.88rem" }}>
                            {s.sharedWithUser.users_pseudo}
                          </Typography>
                          <Typography sx={{ color: col.text, opacity: 0.5, fontSize: "0.72rem" }}>
                            {s.sharedWithUser.users_email}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <>
                        <GroupIcon sx={{ color: col.text, opacity: 0.5, fontSize: "1rem" }} />
                        <Typography sx={{ color: col.text, fontSize: "0.88rem" }}>
                          Groupe : {s.sharedWithGroup?.name}
                        </Typography>
                      </>
                    )}
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveShare(s.id)}
                    sx={{ color: col.text, opacity: 0.4, "&:hover": { opacity: 1, color: "#f44336" } }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Partager avec un utilisateur */}
          <Box>
            <Typography sx={{ color: col.text, fontWeight: 600, mb: 1, fontSize: "0.85rem" }}>
              Ajouter une personne
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField size="small" fullWidth placeholder="Email de l'utilisateur" value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleShareUser()}
                sx={{ "& input": { color: col.text }, "& .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "44" } }} />
              <Button variant="contained" onClick={handleShareUser} disabled={submitting || !email.trim()}
                startIcon={<PersonAddIcon />} sx={{ bgcolor: col.button, whiteSpace: "nowrap" }}>
                Partager
              </Button>
            </Box>
          </Box>

          {/* Partager avec un groupe */}
          {!loadingGroups && myGroups.length > 0 && (
            <Box>
              <Typography sx={{ color: col.text, fontWeight: 600, mb: 1, fontSize: "0.85rem" }}>
                Partager avec un groupe
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                {myGroups.map((g) => {
                  const alreadyShared = sharedGroupIds.has(g.group_id);
                  return (
                    <Box key={g.group_id} sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      p: 1, border: `1px solid ${col.text}22`, borderRadius: 1,
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <GroupIcon sx={{ color: col.text, opacity: 0.6, fontSize: "1rem" }} />
                        <Box>
                          <Typography sx={{ color: col.text, fontSize: "0.88rem" }}>{g.name}</Typography>
                          <Typography sx={{ color: col.text, opacity: 0.45, fontSize: "0.72rem" }}>
                            {g.members?.length || 0} membre(s)
                          </Typography>
                        </Box>
                      </Box>
                      {alreadyShared ? (
                        <Chip label="Partagé" size="small" sx={{ bgcolor: col.button + "44", color: col.text, fontSize: "0.7rem" }} />
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => handleShareGroup(g.group_id)}
                          disabled={submitting}
                          sx={{ color: col.text, borderColor: col.text + "44", fontSize: "0.72rem" }}>
                          Partager
                        </Button>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      {Snack}
    </>
  );
}

// ─── Page Album Detail ────────────────────────────────────────────────────────
export default function AlbumDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state: user } = useContext(AuthContext);
  const theme = useTheme();
  const col = theme.custom.mymodal;
  const { showSnack, Snack } = useSnack();

  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Mode sélection + téléchargement
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [downloading, setDownloading] = useState(false);

  // Édition de label
  const [labelEdit, setLabelEdit] = useState({ id: null, value: "" });

  // Infinite scroll
  const BATCH = 15;
  const [visibleCount, setVisibleCount] = useState(BATCH);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const medias = album?.medias || [];
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((n) => n + BATCH);
      },
      { rootMargin: "600px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [album?.medias?.length]);

  // Upload direct dans l'album (zone "album", pas dans Ma galerie)
  const [pending, setPending] = useState([]); // [{file, label}]
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total } ou null

  useEffect(() => {
    if (!user) { navigate("/connexion"); return; }
    getAlbum(id)
      .then(setAlbum)
      .catch((e) => { showSnack(e.message, "error"); navigate("/albums"); })
      .finally(() => setLoading(false));
  }, [id, user, navigate]); // eslint-disable-line

  // ── Upload direct depuis l'appareil ──
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPending(files.map((file) => ({ file, label: "" })));
    e.target.value = "";
  };

  const handleDirectUpload = async () => {
    const total = pending.length;
    setUploadProgress({ done: 0, total });
    const newIds = [];
    let errors = 0;
    const CONCURRENCY = 5;
    for (let i = 0; i < pending.length; i += CONCURRENCY) {
      const batch = pending.slice(i, i + CONCURRENCY);
      await Promise.allSettled(
        batch.map(async (it) => {
          try {
            const { ID_media: mediaId } = await uploadImage(it.file, "album", it.label);
            newIds.push(mediaId);
          } catch {
            errors++;
          } finally {
            setUploadProgress((prev) => ({ ...prev, done: prev.done + 1 }));
          }
        })
      );
    }
    setPending([]);
    setUploadProgress(null);
    if (newIds.length > 0) {
      try {
        await addMediaToAlbum(album.album_id, newIds);
        const updated = await getAlbum(id);
        setAlbum(updated);
        if (errors === 0) {
          showSnack(`${newIds.length} média(s) ajouté(s) à l'album !`, "success");
        } else {
          showSnack(`${newIds.length} média(s) ajouté(s), ${errors} échec(s).`, "warning");
        }
      } catch (e) {
        showSnack(e.message || "Erreur ajout à l'album", "error");
      }
    } else {
      showSnack(`0 média(s) ajouté(s), ${errors} échec(s).`, "warning");
    }
  };

  // ── Ajout depuis Ma galerie ──
  const handleAddFromGallery = async (mediaIds) => {
    try {
      await addMediaToAlbum(album.album_id, mediaIds);
      const updated = await getAlbum(id);
      setAlbum(updated);
      showSnack(`${mediaIds.length} média(s) ajouté(s) !`, "success");
    } catch (e) { showSnack(e.message, "error"); }
  };

  // ── Retrait d'un média ──
  const handleRemoveMedia = async (img) => {
    const isAlbumOnly = img.zone === "album";
    const isMyImage = img.owner_user_id === user?.users_ID;
    let msg;
    if (album.is_owner && !isMyImage) {
      msg = "Retirer ce média de l'album ? (il restera dans la galerie de son propriétaire)";
    } else if (isAlbumOnly) {
      msg = "Supprimer définitivement ce média ? (il n'est pas dans ta galerie)";
    } else {
      msg = "Retirer ce média de l'album ? (il restera dans ta galerie)";
    }
    if (!window.confirm(msg)) return;
    try {
      await removeMediaFromAlbum(album.album_id, img.ID_media);
      setAlbum((prev) => ({ ...prev, medias: prev.medias.filter((m) => m.ID_media !== img.ID_media) }));
      if (lightboxIdx !== null) setLightboxIdx(null);
      showSnack(isAlbumOnly ? "Média supprimé." : "Média retiré de l'album.", "success");
    } catch (e) { showSnack(e.message, "error"); }
  };

  const toggleSelectMode = () => {
    setSelectMode((prev) => !prev);
    setSelected([]);
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    const medias = album?.medias || [];
    if (selected.length === medias.length) {
      setSelected([]);
    } else {
      setSelected(medias.map((m) => m.ID_media));
    }
  };

  const handleBulkDownload = async () => {
    if (selected.length === 0) return;
    setDownloading(true);
    try {
      if (selected.length === 1) {
        const img = (album?.medias || []).find((m) => m.ID_media === selected[0]);
        triggerSingleDownload(img?.public_id, img?.original_name);
      } else {
        await downloadMediaZip(selected);
      }
    } catch (e) {
      showSnack(e.message || "Erreur téléchargement", "error");
    } finally {
      setDownloading(false);
    }
  };

  const handleLabelSaved = async (mediaId, newLabel) => {
    try {
      await updateLabel(mediaId, newLabel);
      setAlbum((prev) => ({
        ...prev,
        medias: prev.medias.map((m) => m.ID_media === mediaId ? { ...m, label: newLabel } : m),
      }));
      setLabelEdit({ id: null, value: "" });
    } catch (e) {
      showSnack(e.message || "Erreur lors de la sauvegarde", "error");
    }
  };

  const handleShareAdded = (share) => {
    setAlbum((prev) => ({ ...prev, shares: [...(prev.shares || []), share] }));
  };

  const handleShareRemoved = (shareId) => {
    setAlbum((prev) => ({ ...prev, shares: prev.shares.filter((s) => s.id !== shareId) }));
  };

  if (loading) {
    return (
      <>
        <Header />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress sx={{ color: col.text }} />
        </Box>
      </>
    );
  }

  if (!album) return null;

  const albumMediaIds = (album.medias || []).map((m) => m.ID_media);

  return (
    <>
      <Header />
      <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, sm: 3 } }}>

        {/* Breadcrumb + titre */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <IconButton size="small"
            onClick={() => navigate(album.is_owner ? "/albums" : "/shared-with-me")}
            sx={{ color: col.text }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" sx={{ color: col.text, opacity: 0.5 }}>
            {album.is_owner ? "Mes albums" : "Partagé avec moi"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ color: col.text, fontWeight: "bold" }}>
              {album.name}
            </Typography>
            {album.description && (
              <Typography sx={{ color: col.text, opacity: 0.6, mt: 0.3 }}>{album.description}</Typography>
            )}
            {!album.is_owner && (
              <Typography sx={{ color: col.text, opacity: 0.5, fontSize: "0.8rem", mt: 0.5 }}>
                Par {album.owner?.users_pseudo}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {!selectMode && (
              <>
                {/* Upload direct depuis l'appareil (propriétaire et visiteurs) */}
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<PermMediaIcon />}
                  sx={{ color: col.text, borderColor: col.text + "55", "&:hover": { borderColor: col.text } }}
                >
                  Uploader ici
                  <input hidden type="file" accept="image/*,video/*" multiple onChange={handleFileSelect} />
                </Button>
                {/* Depuis Ma galerie (propriétaire et visiteurs) */}
                <Button variant="outlined" startIcon={<CollectionsIcon />}
                  onClick={() => setGalleryDialogOpen(true)}
                  sx={{ color: col.text, borderColor: col.text + "55", "&:hover": { borderColor: col.text } }}>
                  Depuis Ma galerie
                </Button>
                {/* Partager : propriétaire uniquement */}
                {album.is_owner && (
                  <Button variant="outlined" startIcon={<ShareIcon />}
                    onClick={() => setShareDialogOpen(true)}
                    sx={{ color: col.text, borderColor: col.text + "55", "&:hover": { borderColor: col.text } }}>
                    Partager
                    {album.shares?.length > 0 && (
                      <Chip label={album.shares.length} size="small"
                        sx={{ ml: 0.8, bgcolor: col.button, color: "white", height: 18, fontSize: "0.65rem" }} />
                    )}
                  </Button>
                )}
              </>
            )}
            {/* Bouton sélection (disponible pour propriétaire et visiteur) */}
            {(album.medias?.length > 0) && (
              <Button
                variant={selectMode ? "contained" : "outlined"}
                startIcon={selectMode ? <CloseIcon /> : <CheckBoxOutlineBlankIcon />}
                onClick={toggleSelectMode}
                sx={selectMode
                  ? { bgcolor: col.button, color: col.text }
                  : { color: col.text, borderColor: col.text + "55", "&:hover": { borderColor: col.text } }
                }
              >
                {selectMode
                  ? `Annuler${selected.length > 0 ? ` (${selected.length})` : ""}`
                  : "Sélectionner"}
              </Button>
            )}
          </Box>
        </Box>

        {/* Zone pending uploads */}
        {(pending.length > 0 || uploadProgress) && (
          <Box sx={{ mb: 3, p: 2, border: `1px solid ${col.text}33`, borderRadius: 2, bgcolor: col.main }}>
            {uploadProgress ? (
              /* ── Progression en cours ── */
              <Box>
                <Typography sx={{ color: col.text, fontWeight: 600, mb: 1 }}>
                  Envoi en cours… {uploadProgress.done} / {uploadProgress.total}
                </Typography>
                <Box sx={{ width: "100%", height: 6, bgcolor: col.text + "22", borderRadius: 3, overflow: "hidden" }}>
                  <Box sx={{
                    height: "100%", borderRadius: 3,
                    bgcolor: col.button,
                    width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%`,
                    transition: "width 0.3s ease",
                  }} />
                </Box>
              </Box>
            ) : pending.length <= 10 ? (
              /* ── Peu de fichiers : affichage avec labels ── */
              <>
                <Typography sx={{ color: col.text, fontWeight: 600, mb: 1.5 }}>
                  {pending.length} média(s) à uploader dans cet album
                </Typography>
                {pending.map((it, idx) => (
                  <Box key={`${it.file.name}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ color: col.text, fontSize: "0.82rem", minWidth: 180, opacity: 0.8 }}>
                      {it.file.name}
                    </Typography>
                    <TextField
                      size="small" placeholder="Label (optionnel)" value={it.label}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPending((prev) => prev.map((p, i) => i === idx ? { ...p, label: v } : p));
                      }}
                      sx={{
                        flex: 1, minWidth: 180,
                        "& input": { color: col.text, fontSize: "0.82rem" },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "44" },
                      }}
                    />
                  </Box>
                ))}
              </>
            ) : (
              /* ── Beaucoup de fichiers : affichage compact ── */
              <>
                <Typography sx={{ color: col.text, fontWeight: 600, mb: 0.5 }}>
                  {pending.length} médias sélectionnés
                </Typography>
                <Typography sx={{ color: col.text, opacity: 0.6, fontSize: "0.83rem" }}>
                  Les labels peuvent être ajoutés après l'envoi. L'upload se fera en parallèle (5 à la fois).
                </Typography>
              </>
            )}

            {!uploadProgress && (
              <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={handleDirectUpload}
                  sx={{ backgroundColor: col.button, color: col.text }}
                >
                  Envoyer {pending.length} média{pending.length > 1 ? "s" : ""}
                </Button>
                <Button onClick={() => setPending([])} sx={{ color: col.text }}>
                  Annuler
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Grille de photos */}
        {album.medias?.length === 0 ? (
          <Typography sx={{ color: col.text, opacity: 0.5, textAlign: "center", mt: 8 }}>
            {album.is_owner ? "Cet album est vide. Ajoute des médias !" : "Cet album est vide."}
          </Typography>
        ) : (
          <>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
            gap: 2,
            pb: selectMode ? 10 : 0,
          }}>
            {(album.medias || []).slice(0, visibleCount).map((img, idx) => {
              const isSelected = selected.includes(img.ID_media);
              return (
                <Box key={img.ID_media} onClick={selectMode ? () => toggleSelect(img.ID_media) : undefined} sx={{
                  position: "relative", textAlign: "center",
                  cursor: selectMode ? "pointer" : "default",
                  borderRadius: 1,
                  outline: selectMode && isSelected ? `2px solid ${col.button}` : "2px solid transparent",
                  transition: "outline 0.12s",
                  "&:hover .album-actions": { opacity: selectMode ? 0 : 1 },
                  "&:hover .media-thumb": { filter: selectMode ? (isSelected ? "brightness(0.65)" : "brightness(0.88)") : "brightness(0.85)" },
                }}>
                  {/* Checkbox overlay (mode sélection) */}
                  {selectMode && (
                    <Box sx={{
                      position: "absolute", top: 6, left: 6, zIndex: 5,
                      bgcolor: "rgba(0,0,0,0.35)", borderRadius: "50%",
                    }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleSelect(img.ID_media)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={{ color: "white", "&.Mui-checked": { color: col.button }, p: 0.3 }}
                      />
                    </Box>
                  )}

                  {/* Action buttons (mode normal) */}
                  {!selectMode && (
                    <Box className="album-actions" sx={{
                      position: "absolute", top: 6, left: 6, right: 6,
                      display: "flex", justifyContent: "space-between",
                      opacity: 0, transition: "opacity 0.18s ease", zIndex: 5,
                    }}>
                      <IconButton size="small" onClick={() => setLightboxIdx(idx)} sx={{
                        color: "white", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
                        border: "1px solid rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(25,118,210,0.6)" } }}>
                        <ZoomInIcon fontSize="small" />
                      </IconButton>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); triggerSingleDownload(img.public_id, img.original_name); }} sx={{
                          color: "white", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
                          border: "1px solid rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(76,175,80,0.6)" } }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        {(album.is_owner || img.owner_user_id === user?.users_ID) && (
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveMedia(img); }} sx={{
                            color: "white", bgcolor: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
                            border: "1px solid rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(244,67,54,0.6)" } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  )}

                  {img.mime_type?.startsWith("video/") ? (
                    <Box className="media-thumb" onClick={() => { if (!selectMode) setLightboxIdx(idx); }} sx={{
                      width: "100%", height: 180, borderRadius: 1,
                      bgcolor: "rgba(0,0,0,0.75)",
                      border: `1px solid ${col.text}22`,
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.5,
                      cursor: selectMode ? "pointer" : "zoom-in", transition: "filter 0.18s ease",
                    }}>
                      <PlayCircleOutlineIcon sx={{ fontSize: 48, color: "rgba(255,255,255,0.85)" }} />
                      <VideocamIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.4)" }} />
                    </Box>
                  ) : (
                    <Box component="img" src={getMediaUrl(img.public_id)} alt={img.label || "photo"}
                      className="media-thumb"
                      loading="lazy"
                      onClick={() => { if (!selectMode) setLightboxIdx(idx); }}
                      sx={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 1,
                        border: `1px solid ${col.text}22`,
                        cursor: selectMode ? "pointer" : "zoom-in",
                        display: "block", transition: "filter 0.18s ease" }} />
                  )}
                  {!selectMode && img.owner_user_id === user?.users_ID && (
                    <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, px: 0.5 }}>
                      {labelEdit.id === img.ID_media ? (
                        <>
                          <TextField
                            size="small" value={labelEdit.value}
                            onChange={(e) => setLabelEdit((prev) => ({ ...prev, value: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleLabelSaved(img.ID_media, labelEdit.value);
                              if (e.key === "Escape") setLabelEdit({ id: null, value: "" });
                            }}
                            autoFocus
                            sx={{
                              flex: 1,
                              "& input": { color: col.text, fontSize: "0.75rem", py: 0.3, px: 0.8 },
                              "& .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "55" },
                            }}
                          />
                          <IconButton size="small" onClick={() => handleLabelSaved(img.ID_media, labelEdit.value)}
                            sx={{ color: "#4ade80", p: 0.3 }}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setLabelEdit({ id: null, value: "" })}
                            sx={{ color: col.text, p: 0.3 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, cursor: "pointer" }}
                          onClick={() => setLabelEdit({ id: img.ID_media, value: img.label || "" })}>
                          <Typography sx={{ fontSize: "0.75rem", fontStyle: "italic", color: col.text, opacity: 0.8 }}>
                            {img.label || <span style={{ opacity: 0.4 }}>Ajouter un label</span>}
                          </Typography>
                          <EditIcon sx={{ fontSize: "0.8rem", color: col.text, opacity: 0.4 }} />
                        </Box>
                      )}
                    </Box>
                  )}
                  {!selectMode && img.owner_user_id !== user?.users_ID && img.label && (
                    <Typography sx={{ mt: 0.4, fontSize: "0.72rem", fontStyle: "italic", color: col.text, opacity: 0.7,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", px: 0.5 }}>
                      {img.label}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
          {/* Sentinelle infinite scroll */}
          {visibleCount < (album.medias?.length || 0) && (
            <Box ref={sentinelRef} sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} sx={{ color: col.text, opacity: 0.4 }} />
            </Box>
          )}
          </>
        )}
      </Box>

      {lightboxIdx !== null && album.medias?.length > 0 && !selectMode && (
        <Lightbox images={album.medias} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}

      {/* Barre flottante mode sélection */}
      {selectMode && (
        <Box sx={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          zIndex: 200, display: "flex", alignItems: "center", gap: 1.5,
          bgcolor: col.main, border: `1px solid ${col.text}33`,
          borderRadius: 4, px: 3, py: 1.5, boxShadow: 8,
          backdropFilter: "blur(8px)",
        }}>
          <Button
            size="small"
            startIcon={<SelectAllIcon />}
            onClick={selectAll}
            sx={{ color: col.text, fontSize: "0.8rem" }}
          >
            {selected.length === (album?.medias?.length || 0) ? "Désélectionner tout" : "Tout sélectionner"}
          </Button>
          <Box sx={{ width: "1px", height: 24, bgcolor: col.text + "33" }} />
          <Button
            variant="contained"
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleBulkDownload}
            disabled={selected.length === 0 || downloading}
            sx={{ bgcolor: col.button, color: col.text, fontWeight: 600 }}
          >
            {selected.length > 1
              ? `Télécharger (${selected.length}) en ZIP`
              : selected.length === 1
              ? "Télécharger"
              : "Télécharger"}
          </Button>
        </Box>
      )}

      <AddFromGalleryDialog
        open={galleryDialogOpen}
        onClose={() => setGalleryDialogOpen(false)}
        albumMediaIds={albumMediaIds}
        onAdd={handleAddFromGallery}
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        album={album}
        onShareAdded={handleShareAdded}
        onShareRemoved={handleShareRemoved}
      />

      {Snack}
    </>
  );
}
